import KeyIdentifier from '@exodus/key-identifier';
import { derivationPathsWithHardenedPrefix } from './utils/bip32.js';
import BIP32 from '@exodus/bip32';
import BipPath from 'bip32-path';
import { MissingWalletAccountParamError, MissingKeyIdentifierParamError, WalletAccountNotFoundError, PublicKeyNotFoundError, } from '../errors/errors.js';
export class PublicKeyProvider {
    #publicKeyStore;
    #keychain;
    #walletAccountsAtom;
    #logger;
    #getBuildMetadata;
    constructor({ publicKeyStore, keychain, walletAccountsAtom, logger, getBuildMetadata, }) {
        this.#publicKeyStore = publicKeyStore;
        this.#keychain = keychain;
        this.#walletAccountsAtom = walletAccountsAtom;
        this.#logger = logger;
        this.#getBuildMetadata = getBuildMetadata;
    }
    getExtendedPublicKey = async ({ walletAccount, keyIdentifier, }) => {
        const { xpub } = await this.#exportPublic({ walletAccount, keyIdentifier });
        return xpub;
    };
    getPublicKey = async ({ walletAccount, keyIdentifier, }) => {
        const { publicKey } = await this.#exportPublic({ walletAccount, keyIdentifier });
        return publicKey;
    };
    /**
     * Gradually chops of one part of the derivation path and check if we have an xpub stored for the reduced path.
     * If we find one, we derive the requested public key from the xpub using the partial path that was chopped of from the initially requested derivation path.
     * We stop traversing at the first hardened index, because hardened derivation is not possible without a private key.
     */
    #traversePathForXpub = async ({ keyIdentifier, walletAccountName }) => {
        if (keyIdentifier.derivationAlgorithm === 'BIP32' && keyIdentifier.keyType === 'secp256k1') {
            for (const { derivationPath, removedIndices } of derivationPathsWithHardenedPrefix(keyIdentifier.derivationPath)) {
                const xpubKeyIdentifier = new KeyIdentifier({
                    ...keyIdentifier,
                    derivationPath,
                });
                const entry = await this.#publicKeyStore.get({
                    walletAccountName,
                    keyIdentifier: xpubKeyIdentifier,
                });
                if (entry?.xpub) {
                    const path = BipPath.fromPathArray(removedIndices.map(Number)).toString();
                    const { publicKey, xPub: xpub } = BIP32.fromXPub(entry.xpub).derive(path);
                    return { publicKey, xpub };
                }
            }
        }
    };
    #getCachedPublicKey = async (params) => {
        const cached = await this.#publicKeyStore.get(params);
        if (cached) {
            return cached;
        }
        return this.#traversePathForXpub(params);
    };
    #exportPublic = async ({ walletAccount: walletAccountName, keyIdentifier, }) => {
        if (!walletAccountName) {
            throw new MissingWalletAccountParamError();
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!keyIdentifier) {
            throw new MissingKeyIdentifierParamError();
        }
        // backwards compat for callers that today pass a WalletAccount instance
        if (typeof walletAccountName !== 'string') {
            this.#logger.warn('expected walletAccount to be a string', walletAccountName);
            walletAccountName = walletAccountName.toString();
        }
        const walletAccounts = await this.#walletAccountsAtom.get();
        const walletAccount = walletAccounts[walletAccountName];
        if (!walletAccount) {
            throw new WalletAccountNotFoundError(walletAccountName);
        }
        // Ensure key identifier is of the right type and frozen
        keyIdentifier = new KeyIdentifier(keyIdentifier);
        const buildMetadata = await this.#getBuildMetadata();
        if (buildMetadata.dev || walletAccount.isHardware) {
            const cached = await this.#getCachedPublicKey({
                keyIdentifier,
                walletAccountName,
            });
            if (cached) {
                return cached;
            }
        }
        if (walletAccount.isSoftware) {
            const { publicKey, xpub } = await this.#keychain.exportKey({
                keyId: keyIdentifier,
                seedId: walletAccount.seedId,
                exportPrivate: false,
            });
            // Don't wait to avoid extra latency
            void this.#publicKeyStore.add({
                walletAccount,
                keyIdentifier,
                publicKey,
                xpub,
            });
            return { publicKey, xpub };
        }
        throw new PublicKeyNotFoundError(walletAccountName, keyIdentifier.derivationPath, keyIdentifier.derivationAlgorithm);
    };
}
const createPublicKeyProvider = (dependencies) => new PublicKeyProvider(dependencies);
const publicKeyProviderDefinition = {
    id: 'publicKeyProvider',
    type: 'module',
    factory: createPublicKeyProvider,
    dependencies: ['publicKeyStore', 'keychain', 'walletAccountsAtom', 'logger', 'getBuildMetadata'],
    public: true,
};
export default publicKeyProviderDefinition;
